import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { useAuth } from './auth/useAuth.js';
import { useMe, useTenants } from './api/identity.js';
import { useActiveTenant } from './auth/useActiveTenant.js';
import type { Membership, Tenant } from './api/types.js';
import { LoginScreen } from './screens/LoginScreen.js';
import { InvoiceListScreen } from './screens/invoices/InvoiceListScreen.js';
import { InvoiceDetail } from './screens/invoices/InvoiceDetail.js';
import { ValidationsScreen } from './screens/validations/ValidationsScreen.js';
import { UploadScreen } from './screens/upload/UploadScreen.js';
import { NewInvoiceWizard } from './screens/outbound/NewInvoiceWizard.js';
import { DashboardScreen } from './screens/dashboard/DashboardScreen.js';
import { AnnuaireScreen } from './screens/annuaire/AnnuaireScreen.js';
import { TenantSettingsScreen } from './screens/settings/TenantSettingsScreen.js';
import { TenantOverviewScreen } from './screens/tenant/TenantOverviewScreen.js';
import { TenantStatsScreen } from './screens/stats/TenantStatsScreen.js';
import { AdminMembersScreen } from './screens/admin/AdminMembersScreen.js';
import { AdminAuditScreen } from './screens/admin/AdminAuditScreen.js';
import { AdminSettingsScreen } from './screens/admin/AdminSettingsScreen.js';
import { isGroupAdmin, isValidatorOnly } from './lib/role.js';
import { useIsWide } from './lib/breakpoints.js';
import { AppShell } from './shell/AppShell.js';

function LoadingScreen({ label }: { label: string }) {
  return (
    <div className="app-loading" role="status">
      {label}
    </div>
  );
}

function OverviewPlaceholder() {
  return (
    <section>
      <div className="eyebrow">Portefeuille</div>
      <h1 className="display" style={{ fontSize: 28, margin: '4px 0 16px' }}>
        Vue d&apos;ensemble multi-sociétés
      </h1>
      <p className="muted" style={{ maxWidth: 560 }}>
        Sélectionnez une société dans la barre latérale pour ouvrir son inbox. Les KPIs cross-tenant
        arriveront en S1.
      </p>
    </section>
  );
}

interface IdentityState {
  tenants: Tenant[];
  memberships: Membership[];
}

function OverviewRoute({ identity }: { identity: IdentityState }) {
  const active = useActiveTenant(identity.tenants, identity.memberships);
  // Post-sign-in landing: dashboard if the user has any membership;
  // otherwise the placeholder overview screen.
  if (identity.memberships.length > 0) {
    return <Navigate to="/dashboard" replace />;
  }
  return (
    <AppShell active={active} crumb="Vue d'ensemble">
      <OverviewPlaceholder />
    </AppShell>
  );
}

function ValidatorInvoiceDetail({
  fallbackTenantName,
  role,
}: {
  fallbackTenantName: string;
  role: Membership['role'] | null;
}) {
  const { tenantId } = useParams<{ tenantId: string }>();
  if (!tenantId) return <Navigate to="/dashboard" replace />;
  return <InvoiceDetail tenantId={tenantId} tenantName={fallbackTenantName} role={role} />;
}

function DashboardRoute({ identity }: { identity: IdentityState }) {
  const active = useActiveTenant(identity.tenants, identity.memberships);
  const validatorOnly = isValidatorOnly(identity.memberships);
  const showAdmin = isGroupAdmin(identity.memberships);
  return (
    <AppShell
      active={active}
      crumb="Tableau de bord"
      validatorOnly={validatorOnly}
      showAdminMembers={showAdmin}
    >
      <DashboardScreen tenants={identity.tenants} memberships={identity.memberships} />
    </AppShell>
  );
}

function AdminMembersRoute({ identity }: { identity: IdentityState }) {
  const active = useActiveTenant(identity.tenants, identity.memberships);
  if (!isGroupAdmin(identity.memberships)) {
    return <Navigate to="/dashboard" replace />;
  }
  return (
    <AppShell active={active} crumb="Administration · Membres" showAdminMembers>
      <AdminMembersScreen tenants={identity.tenants} memberships={identity.memberships} />
    </AppShell>
  );
}

function AdminAuditRoute({ identity }: { identity: IdentityState }) {
  const active = useActiveTenant(identity.tenants, identity.memberships);
  if (!isGroupAdmin(identity.memberships)) {
    return <Navigate to="/dashboard" replace />;
  }
  return (
    <AppShell active={active} crumb="Administration · Audit" showAdminMembers>
      <AdminAuditScreen tenants={identity.tenants} memberships={identity.memberships} />
    </AppShell>
  );
}

// REQ-FE-029j: /admin/settings placeholder route (Iopole connection +
// Notifications + Audit cards). Group-admin gated like the other admin
// routes.
function AdminSettingsRoute({ identity }: { identity: IdentityState }) {
  const active = useActiveTenant(identity.tenants, identity.memberships);
  if (!isGroupAdmin(identity.memberships)) {
    return <Navigate to="/dashboard" replace />;
  }
  return (
    <AppShell active={active} crumb="Administration · Paramètres" showAdminMembers>
      <AdminSettingsScreen />
    </AppShell>
  );
}

// REQ-FE-029a: switches between split-pane (≥1280px renders the list with
// detail selected in the centre + history/PDF rail on the right) and
// standalone detail (<1280px falls back to the original single-pane
// detail screen). The `:primaryKey` URL param is the source of truth for
// selection in both modes.
function InvoiceListDetailRoute({
  tenantId,
  tenantName,
  role,
}: {
  tenantId: string;
  tenantName: string;
  role: Membership['role'] | null;
}) {
  const isWide = useIsWide();
  if (isWide) {
    return <InvoiceListScreen tenantId={tenantId} tenantName={tenantName} role={role} />;
  }
  return <InvoiceDetail tenantId={tenantId} tenantName={tenantName} role={role} />;
}

function TenantRoute({ identity }: { identity: IdentityState }) {
  const active = useActiveTenant(identity.tenants, identity.memberships);
  const validatorOnly = isValidatorOnly(identity.memberships);
  const showAdmin = isGroupAdmin(identity.memberships);

  // Validator-only callers may only deep-link to a single invoice's
  // detail view (so they can validate it). Every other per-tenant
  // route (list, validations index, upload, new invoice, annuaire,
  // settings) bounces back to the personal dashboard.
  if (validatorOnly) {
    return (
      <Routes>
        <Route
          path="invoices/:primaryKey"
          element={
            <AppShell active={active} crumb="Facture" validatorOnly>
              <ValidatorInvoiceDetail
                fallbackTenantName={active.tenant?.legalName ?? '—'}
                role={active.membership?.role ?? null}
              />
            </AppShell>
          }
        />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    );
  }

  if (!active.tenant) {
    if (active.available.length > 0) {
      return <Navigate to={`/t/${active.available[0]!.tenantId}`} replace />;
    }
    return (
      <AppShell active={active}>
        <section>
          <h1 className="display" style={{ fontSize: 24, margin: 0 }}>
            Société introuvable
          </h1>
          <p className="muted">Vous n&apos;avez pas accès à cette société.</p>
        </section>
      </AppShell>
    );
  }

  return (
    <Routes>
      <Route
        index
        element={
          // REQ-FE-029b: per-tenant landing — KPI row + 7-card navigation grid.
          // Replaces the previous auto-redirect to `invoices`. Click "Boîte de
          // réception" card to reach the invoice list.
          <AppShell active={active} crumb="Vue d'ensemble" showAdminMembers={showAdmin}>
            <TenantOverviewScreen
              tenantId={active.tenant.tenantId}
              tenantName={active.tenant.legalName}
            />
          </AppShell>
        }
      />
      <Route
        path="invoices"
        element={
          <AppShell active={active} crumb="Inbox" showAdminMembers={showAdmin}>
            <InvoiceListScreen
              tenantId={active.tenant.tenantId}
              tenantName={active.tenant.legalName}
              role={active.membership?.role ?? null}
            />
          </AppShell>
        }
      />
      <Route
        path="invoices/:primaryKey"
        element={
          // REQ-FE-029a: at ≥1280px the list+detail render as split-pane via
          // InvoiceListScreen (it reads :primaryKey from useParams and
          // mounts InvoiceDetail in centre + InvoiceDetailRail on the right).
          // Below 1280px the list collapses to a single pane and we render
          // the standalone InvoiceDetail screen here instead.
          <AppShell active={active} crumb="Inbox" showAdminMembers={showAdmin}>
            <InvoiceListDetailRoute
              tenantId={active.tenant.tenantId}
              tenantName={active.tenant.legalName}
              role={active.membership?.role ?? null}
            />
          </AppShell>
        }
      />
      <Route
        path="validations"
        element={
          <AppShell active={active} crumb="Validations" showAdminMembers={showAdmin}>
            <ValidationsScreen
              tenantId={active.tenant.tenantId}
              tenantName={active.tenant.legalName}
            />
          </AppShell>
        }
      />
      <Route
        path="upload"
        element={
          <AppShell active={active} crumb="Importer" showAdminMembers={showAdmin}>
            <UploadScreen tenantId={active.tenant.tenantId} tenantName={active.tenant.legalName} />
          </AppShell>
        }
      />
      <Route
        path="invoices/new"
        element={
          <AppShell active={active} crumb="Créer" showAdminMembers={showAdmin}>
            <NewInvoiceWizard
              tenantId={active.tenant.tenantId}
              tenantName={active.tenant.legalName}
            />
          </AppShell>
        }
      />
      <Route
        path="annuaire"
        element={
          <AppShell active={active} crumb="Annuaire" showAdminMembers={showAdmin}>
            <AnnuaireScreen
              tenantId={active.tenant.tenantId}
              tenantName={active.tenant.legalName}
            />
          </AppShell>
        }
      />
      {/* REQ-FE-029d: /suppliers alias of /annuaire matching mockup
          terminology. Same component, same crumb — old links keep
          working, new links use the mockup path. */}
      <Route
        path="suppliers"
        element={
          <AppShell active={active} crumb="Annuaire" showAdminMembers={showAdmin}>
            <AnnuaireScreen
              tenantId={active.tenant.tenantId}
              tenantName={active.tenant.legalName}
            />
          </AppShell>
        }
      />
      <Route
        path="stats"
        element={
          // REQ-FE-029f: dedicated stats page (Indicateurs). Reachable
          // from the tenant overview Statistiques card.
          <AppShell active={active} crumb="Indicateurs" showAdminMembers={showAdmin}>
            <TenantStatsScreen
              tenantId={active.tenant.tenantId}
              tenantName={active.tenant.legalName}
            />
          </AppShell>
        }
      />
      <Route
        path="settings"
        element={
          <AppShell active={active} crumb="Paramètres" showAdminMembers={showAdmin}>
            <TenantSettingsScreen
              tenantId={active.tenant.tenantId}
              tenantName={active.tenant.legalName}
              callerRole={active.membership?.role ?? null}
            />
          </AppShell>
        }
      />
      <Route path="*" element={<Navigate to={`/t/${active.tenant.tenantId}`} replace />} />
    </Routes>
  );
}

function AuthenticatedApp() {
  const me = useMe();
  const tenants = useTenants();

  if (me.isLoading || tenants.isLoading) {
    return <LoadingScreen label="Chargement de votre compte" />;
  }

  if (me.isError || tenants.isError || !me.data || !tenants.data) {
    return (
      <div className="app-loading" role="alert">
        Impossible de charger votre compte. Réessayez dans un instant.
      </div>
    );
  }

  const identity: IdentityState = {
    tenants: tenants.data,
    memberships: me.data.memberships,
  };

  return (
    <Routes>
      <Route path="/" element={<OverviewRoute identity={identity} />} />
      <Route path="/dashboard" element={<DashboardRoute identity={identity} />} />
      <Route path="/admin/members" element={<AdminMembersRoute identity={identity} />} />
      <Route path="/admin/audit" element={<AdminAuditRoute identity={identity} />} />
      <Route path="/admin/settings" element={<AdminSettingsRoute identity={identity} />} />
      <Route path="/t/:tenantId/*" element={<TenantRoute identity={identity} />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export function App() {
  const { user, loading } = useAuth();

  if (loading) return <LoadingScreen label="Chargement" />;
  if (!user) return <LoginScreen />;
  return <AuthenticatedApp />;
}
